// apps/curbpass/src/routes/vehicles/VehiclesPageContent.tsx
import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { RegisteredVehicle } from "common/graphql";
import { EditBuffer, NewVehicleRow } from "./vehicles.types";
import { BulkSubmitResult } from "./hooks/useBulkSubmit";
import { ImportSection } from "./components/ImportSection";
import { VehiclesTable } from "./components/VehiclesTable";
import { SubmitResultBanner } from "./components/SubmitResultBanner";

export interface VehiclesPageContentProps {
  existingVehicles: RegisteredVehicle[];
  vehiclesLoading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearchChange: (value: string) => void;
  newRows: NewVehicleRow[];
  onNewRowsChange: (rows: NewVehicleRow[]) => void;
  onRowsParsed: (rows: NewVehicleRow[]) => void;
  onDeleteExisting: (ids: string[]) => Promise<void>;
  onSaveEdit: (id: string, data: EditBuffer) => Promise<void>;
  onSubmitNew: (
    data: Pick<NewVehicleRow, "stateCode" | "licensePlate" | "make" | "model" | "type" | "category">,
  ) => Promise<void>;
  onSubmit: () => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  progress: { current: number; total: number };
  bannerResult: BulkSubmitResult | null;
  onDismissBanner: () => void;
}

export const VehiclesPageContent: React.FC<VehiclesPageContentProps> = ({
  existingVehicles,
  vehiclesLoading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  newRows,
  onNewRowsChange,
  onRowsParsed,
  onDeleteExisting,
  onSaveEdit,
  onSubmitNew,
  onSubmit,
  canSubmit,
  isSubmitting,
  progress,
  bannerResult,
  onDismissBanner,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, width: "100%", mx: "auto", display: "flex", flexDirection: "column", flex: 1 }}>
      <Typography variant="h4" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Vehicles
      </Typography>

      {bannerResult && (
        <SubmitResultBanner
          successCount={bannerResult.successCount}
          failedRows={bannerResult.failedRows}
          networkError={bannerResult.networkError}
          onDismiss={onDismissBanner}
        />
      )}

      {!isMobile && <ImportSection onRowsParsed={onRowsParsed} disabled={isSubmitting} />}

      {isSubmitting && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Processing… ({progress.current} of {progress.total} batches)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <VehiclesTable
          existingVehicles={existingVehicles}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onSearchChange={onSearchChange}
          newRows={newRows}
          onNewRowsChange={onNewRowsChange}
          onDeleteExisting={onDeleteExisting}
          onSaveEdit={onSaveEdit}
          onSubmitNew={onSubmitNew}
          loading={vehiclesLoading}
          disabled={isSubmitting}
          footerActions={
            !isMobile ? (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mr: 1 }}>
                {newRows.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {newRows.length} new vehicle{newRows.length !== 1 ? "s" : ""} to add
                  </Typography>
                )}
                <Button variant="contained" disabled={!canSubmit} onClick={onSubmit}>
                  Submit
                </Button>
              </Stack>
            ) : undefined
          }
        />
      </Box>
    </Box>
  );
};

export default VehiclesPageContent;
